import { useState, useEffect, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { Tag, Package, Stack, Info, Percent, Calculator, Stairs, Megaphone } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { PageHeader } from "@/components/PageHeader";
import { LoadingSpinner } from "@/components/LoadingSpinner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatPrice = (price, currency) => {
  if (price === null || price === undefined || price === "") return "-";
  try {
    return new Intl.NumberFormat('pt-PT', { style: 'currency', currency: currency || 'EUR' }).format(price);
  } catch {
    return `${price} ${currency || ''}`;
  }
};

const formatNumber = (value, suffix = "") => {
  if (value === null || value === undefined || value === "") return "-";
  if (typeof value === "number") {
    return `${new Intl.NumberFormat('pt-PT').format(value)}${suffix}`;
  }
  return `${value}${suffix}`;
};

const Section = ({ icon: Icon, title, children }) => (
  <div className="gresart-card">
    <h3 className="text-sm font-bold text-stone-700 uppercase tracking-wide flex items-center gap-2 mb-4">
      <Icon size={16} className="text-[#009392]" />
      {title}
    </h3>
    <div className="grid grid-cols-2 sm:grid-cols-3 gap-4">{children}</div>
  </div>
);

const Field = ({ label, value }) => (
  <div data-testid={`preco-field-${label.toLowerCase().replace(/\s+/g, '-')}`}>
    <p className="text-xs text-stone-400 mb-0.5">{label}</p>
    <p className="text-sm text-stone-800 font-medium">{value || "-"}</p>
  </div>
);

// Descontos em cascata (cada % aplicado sobre o valor já descontado pelo anterior)
const DISCOUNT_TIERS = [
  { label: "30%", rates: [0.30] },
  { label: "30% + 5%", rates: [0.30, 0.05] },
  { label: "30% + 5% + 5%", rates: [0.30, 0.05, 0.05] },
];

const applyCascadingDiscount = (price, rates) => rates.reduce((acc, rate) => acc * (1 - rate), price);

export default function PrecoDetail() {
  const { productId } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [extras, setExtras] = useState(null);
  const [loading, setLoading] = useState(true);
  const [areaInput, setAreaInput] = useState("");

  useEffect(() => {
    const fetchProduct = async () => {
      try {
        const response = await axios.get(`${API}/price-products/${productId}`);
        setProduct(response.data);
        try {
          const extrasResponse = await axios.get(`${API}/price-products/${productId}/extras`);
          setExtras(extrasResponse.data);
        } catch (extrasError) {
          console.error("Error fetching price extras:", extrasError);
        }
      } catch (error) {
        console.error("Error fetching price product:", error);
      } finally {
        setLoading(false);
      }
    };
    fetchProduct();
  }, [productId]);

  // Calculadora de m²: a partir da área pretendida, calcula caixas necessárias (arredondado
  // para cima, já que só é possível comprar caixas inteiras) e o preço total correspondente.
  const areaCalc = useMemo(() => {
    if (!product) return null;
    const area = parseFloat(String(areaInput).replace(',', '.'));
    const m2PerBox = parseFloat(product.m2_per_box);
    const price = parseFloat(product.price);
    if (!area || area <= 0 || !m2PerBox || m2PerBox <= 0 || !price) return null;
    const boxesNeeded = Math.ceil(area / m2PerBox);
    const totalAreaCovered = boxesNeeded * m2PerBox;
    const unit = (product.price_unit || "").toUpperCase();
    let pricePerBox = price;
    if (unit === "M2") {
      pricePerBox = price * m2PerBox;
    } else if (unit === "PC" && product.pieces_per_box) {
      pricePerBox = price * parseFloat(product.pieces_per_box);
    }
    return { boxesNeeded, totalAreaCovered, totalPrice: boxesNeeded * pricePerBox };
  }, [areaInput, product]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[300px]">
        <LoadingSpinner size={40} />
      </div>
    );
  }

  if (!product) {
    return (
      <div className="empty-state py-16 bg-stone-50 rounded-xl text-center" data-testid="preco-detail-not-found">
        <Tag size={56} className="text-stone-300 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-stone-700 mb-2">Produto não encontrado</h3>
        <Button variant="outline" onClick={() => navigate('/precos')} className="mt-2">Voltar aos Preços</Button>
      </div>
    );
  }

  return (
    <div className="space-y-6 pb-24 md:pb-6" data-testid="preco-detail-page">
      <PageHeader
        icon={Tag}
        title="Ficha do Produto"
        subtitle={product.product_code}
      />

      {/* Nome e preço em destaque */}
      <div className="gresart-card bg-gradient-to-br from-[#009392]/5 to-white border-[#009392]/20" data-testid="preco-detail-highlight">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-xl sm:text-2xl font-bold text-stone-900 mb-3 leading-snug">{product.product_name || "Produto sem nome"}</h2>
            <div className="flex items-baseline gap-2">
              <span className="text-3xl sm:text-4xl font-extrabold text-[#009392]" data-testid="preco-detail-price">
                {formatPrice(product.price, product.currency)}
              </span>
              {product.price_unit && <span className="text-base text-stone-500">/ {product.price_unit}</span>}
            </div>
          </div>
          <div className="flex items-center gap-2 flex-shrink-0">
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="border-[#009392]/40 text-[#009392] hover:bg-[#009392]/10" data-testid="btn-calculadora-area">
                  <Calculator size={16} weight="bold" />
                  Calculadora
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-80" data-testid="calculadora-popover">
                <p className="text-xs font-bold text-stone-400 uppercase tracking-wide mb-3">Calculadora de Área</p>
                <Label htmlFor="area-necessaria" className="text-xs text-stone-500 mb-1 block">Área Necessária (m²)</Label>
                <Input
                  id="area-necessaria"
                  type="number"
                  min="0"
                  step="0.01"
                  placeholder="Ex: 25"
                  value={areaInput}
                  onChange={(e) => setAreaInput(e.target.value)}
                  className="h-11 mb-3"
                  data-testid="input-area-necessaria"
                  autoFocus
                />
                {areaCalc && (
                  <div className="space-y-3" data-testid="resultado-calculadora-m2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-stone-500">Caixas Necessárias</span>
                      <span className="font-bold text-stone-900" data-testid="resultado-caixas-necessarias">{areaCalc.boxesNeeded}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-stone-500">Área Total (arredondada)</span>
                      <span className="font-bold text-stone-900" data-testid="resultado-area-total">{formatNumber(Math.round(areaCalc.totalAreaCovered * 100) / 100, " m²")}</span>
                    </div>
                    <div className="flex items-center justify-between text-sm pt-2 border-t border-stone-100">
                      <span className="text-stone-500">Preço Total</span>
                      <span className="font-bold text-stone-900" data-testid="resultado-preco-total">{formatPrice(areaCalc.totalPrice, product.currency)}</span>
                    </div>
                    <p className="text-xs font-bold text-stone-400 uppercase tracking-wide pt-2 border-t border-stone-100">Preço Total com Desconto</p>
                    {DISCOUNT_TIERS.map((tier) => (
                      <div key={tier.label} className="flex items-center justify-between text-sm" data-testid={`resultado-desconto-${tier.label.replace(/[\s%+]/g, '')}`}>
                        <span className="text-stone-500">{tier.label}</span>
                        <span className="font-bold text-[#009392]">{formatPrice(applyCascadingDiscount(areaCalc.totalPrice, tier.rates), product.currency)}</span>
                      </div>
                    ))}
                  </div>
                )}
                {!areaCalc && areaInput && (
                  <p className="text-sm text-stone-400" data-testid="calculadora-sem-resultado">Introduza um valor válido para calcular.</p>
                )}
              </PopoverContent>
            </Popover>
            <Popover>
              <PopoverTrigger asChild>
                <Button variant="outline" className="border-[#009392]/40 text-[#009392] hover:bg-[#009392]/10" data-testid="btn-descontos">
                  <Percent size={16} weight="bold" />
                  Descontos
                </Button>
              </PopoverTrigger>
              <PopoverContent align="end" className="w-64" data-testid="descontos-popover">
                <p className="text-xs font-bold text-stone-400 uppercase tracking-wide mb-3">Preços com Desconto</p>
                <div className="space-y-3">
                  {DISCOUNT_TIERS.map((tier) => (
                    <div key={tier.label} className="flex items-center justify-between" data-testid={`desconto-tier-${tier.label.replace(/[\s%+]/g, '')}`}>
                      <span className="text-sm text-stone-500">{tier.label}</span>
                      <span className="text-base font-bold text-stone-900">
                        {formatPrice(applyCascadingDiscount(product.price, tier.rates), product.currency)}
                      </span>
                    </div>
                  ))}
                </div>
              </PopoverContent>
            </Popover>
          </div>
        </div>
      </div>

      {(extras?.volume_2tier || extras?.volume_1tier) && (
        <div className="gresart-card" data-testid="section-precos-volume">
          <h3 className="text-sm font-bold text-stone-700 uppercase tracking-wide flex items-center gap-2 mb-4">
            <Stairs size={16} className="text-[#009392]" />
            Preços por Volume
          </h3>
          <div className="space-y-2">
            {extras.volume_2tier && (
              <>
                <div className="flex items-center justify-between bg-stone-50 rounded-lg px-4 py-2.5" data-testid="volume-2tier-tier1">
                  <span className="text-sm text-stone-600">2 Paletes por Referência</span>
                  <span className="text-base font-bold text-stone-900">{formatPrice(extras.volume_2tier.price_2_paletes, extras.volume_2tier.currency)} / m²</span>
                </div>
                <div className="flex items-center justify-between bg-stone-50 rounded-lg px-4 py-2.5" data-testid="volume-2tier-tier2">
                  <span className="text-sm text-stone-600">5 Paletes por Referência</span>
                  <span className="text-base font-bold text-stone-900">{formatPrice(extras.volume_2tier.price_5_paletes, extras.volume_2tier.currency)} / m²</span>
                </div>
              </>
            )}
            {extras.volume_1tier && (
              <div className="flex items-center justify-between bg-stone-50 rounded-lg px-4 py-2.5" data-testid="volume-1tier-tier">
                <span className="text-sm text-stone-600">Mínimo 10 Paletes (mín. 2 por referência)</span>
                <span className="text-base font-bold text-stone-900">{formatPrice(extras.volume_1tier.price, extras.volume_1tier.currency)} / m²</span>
              </div>
            )}
          </div>
        </div>
      )}

      {extras?.special_campaign && (
        <div className="gresart-card bg-amber-50/40 border-amber-200" data-testid="section-precos-especiais">
          <h3 className="text-sm font-bold text-stone-700 uppercase tracking-wide flex items-center gap-2 mb-1">
            <Megaphone size={16} className="text-amber-600" />
            Preços Especiais
          </h3>
          <p className="text-xs text-amber-700 mb-4">{extras.special_campaign.campaign_name}</p>
          <div className="space-y-2">
            <div className="flex items-center justify-between bg-white rounded-lg px-4 py-2.5 border border-amber-100" data-testid="special-campaign-tier1">
              <span className="text-sm text-stone-600">5 Paletes por Referência</span>
              <span className="text-base font-bold text-stone-900">{formatPrice(extras.special_campaign.price_5_paletes, extras.special_campaign.currency)} / m²</span>
            </div>
            <div className="flex items-center justify-between bg-white rounded-lg px-4 py-2.5 border border-amber-100" data-testid="special-campaign-tier2">
              <span className="text-sm text-stone-600">10 Paletes por Referência</span>
              <span className="text-base font-bold text-stone-900">{formatPrice(extras.special_campaign.price_10_paletes, extras.special_campaign.currency)} / m²</span>
            </div>
            <p className="text-xs text-stone-400 pt-1">Mínimo {extras.special_campaign.min_pallets_per_reference} paletes por referência</p>
          </div>
        </div>
      )}

      {extras?.outras_escolhas?.length > 0 && (
        <div className="gresart-card" data-testid="section-outras-escolhas">
          <h3 className="text-sm font-bold text-stone-700 uppercase tracking-wide flex items-center gap-2 mb-4">
            <Tag size={16} className="text-[#009392]" />
            Preços Outras Escolhas
          </h3>
          <div className="space-y-2">
            {extras.outras_escolhas.map((item, idx) => (
              <div key={idx} className="flex items-center justify-between bg-stone-50 rounded-lg px-4 py-2.5" data-testid={`outras-escolhas-item-${idx}`}>
                <span className="text-sm text-stone-600">
                  {item.escolha}ª Escolha · {item.rectified ? "Retificado" : "Não Retificado"}
                </span>
                <div className="flex items-center gap-2">
                  <span className="text-base font-bold text-stone-900">{formatPrice(item.price, item.currency)} / m²</span>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-7 w-7 border-[#009392]/40 text-[#009392] hover:bg-[#009392]/10"
                        data-testid={`btn-descontos-outras-escolhas-${idx}`}
                      >
                        <Percent size={14} weight="bold" />
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent align="end" className="w-64" data-testid={`descontos-outras-escolhas-popover-${idx}`}>
                      <p className="text-xs font-bold text-stone-400 uppercase tracking-wide mb-3">Preços com Desconto</p>
                      <div className="space-y-3">
                        {DISCOUNT_TIERS.map((tier) => (
                          <div key={tier.label} className="flex items-center justify-between" data-testid={`desconto-outras-escolhas-${idx}-${tier.label.replace(/[\s%+]/g, '')}`}>
                            <span className="text-sm text-stone-500">{tier.label}</span>
                            <span className="text-base font-bold text-stone-900">
                              {formatPrice(applyCascadingDiscount(item.price, tier.rates), item.currency)}
                            </span>
                          </div>
                        ))}
                      </div>
                    </PopoverContent>
                  </Popover>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <Section icon={Info} title="Características do Produto">
        <Field label="Cor" value={product.color} />
        <Field label="Formato" value={product.format} />
        <Field label="Série" value={product.series} />
        <Field label="Tipo de Utilização" value={product.usage_type} />
        <Field label="Acabamento / Superfície" value={product.finish_surface} />
        <Field label="Espessura" value={product.thickness} />
        <Field label="Marca" value={product.brand} />
        <Field label="Qualidade" value={product.quality} />
      </Section>

      <Section icon={Package} title="Embalagem e Logística">
        <Field label="Unidade de Medida" value={product.sale_unit} />
        <Field label="Peças por Caixa" value={formatNumber(product.pieces_per_box)} />
        <Field label="m² por Caixa" value={formatNumber(product.m2_per_box)} />
        <Field label="kg por Caixa" value={formatNumber(product.kg_per_box)} />
        <Field label="Caixas por Palete" value={formatNumber(product.boxes_per_pallet)} />
        <Field label="m² por Palete" value={formatNumber(product.m2_per_pallet)} />
        <Field label="kg por Palete" value={formatNumber(product.kg_per_pallet)} />
      </Section>

      <Section icon={Stack} title="Outras Informações">
        <Field label="EAN" value={product.ean} />
        <Field label="País de Origem" value={product.country_origin} />
        <Field label="Grupo Preço" value={product.price_group} />
      </Section>
    </div>
  );
}
